// src/components/Manager/TachesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter,
  Search,
  Calendar,
  User,
  Flag,
  Trash2,
  X,
} from 'lucide-react';
import Layout from '../Layout';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem('access_token');
const getAuthData = () => JSON.parse(localStorage.getItem('auth_data') || '{}');

export default function TachesPage() {
  const navigate = useNavigate();
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    a_faire: 0,
    en_cours: 0,
    terminees: 0,
    en_retard: 0
  });
  const [filters, setFilters] = useState({
    statut: 'tous',
    priorite: 'tous',
    recherche: ''
  });

  // États pour le popup d'ajout
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [newTask, setNewTask] = useState({
    description: '',
    reunion_id: '',
    responsable_id: '',
    deadline: '',
    priorite: 'moyenne'
  });
  const [reunions, setReunions] = useState([]);
  const [addingTask, setAddingTask] = useState(false);
  const [loadingReunions, setLoadingReunions] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [reunionParticipants, setReunionParticipants] = useState([]);

  // États pour le popup d'édition
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskData, setEditTaskData] = useState({
    description: '',
    reunion_id: '',
    responsable_id: '',
    deadline: '',
    priorite: 'moyenne',
    statut: 'a_faire'
  });
  const [updatingTask, setUpdatingTask] = useState(false);

  // État pour le popup de confirmation de suppression
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authData = getAuthData();
  const isAdminOrManager = authData.role === 'Admin' || authData.role === 'Manager';

  useEffect(() => {
    fetchTaches();
    fetchStats();
    if (isAdminOrManager) {
      fetchAllUsers();
    }
  }, []);

  useEffect(() => {
    fetchTaches();
  }, [filters]);

  // Récupérer les participants d'une réunion spécifique
  const fetchReunionParticipants = async (reunionId) => {
    if (!reunionId) {
      setReunionParticipants([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/reunions/${reunionId}/participants_list/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Participants de la réunion:", data);
        setReunionParticipants(data);
      } else {
        setReunionParticipants([]);
      }
    } catch (error) {
      console.error("Erreur chargement participants:", error);
      setReunionParticipants([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Récupérer tous les utilisateurs (pour Admin/Manager)
  const fetchAllUsers = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/users/?page_size=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        let usersList = [];
        if (data.results) usersList = data.results;
        else if (Array.isArray(data)) usersList = data;
        setUsers(usersList);
      }
    } catch (error) {
      console.error("Erreur chargement users:", error);
      setUsers([]);
    }
  };

  const fetchTaches = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) return;

      let allTasks = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        let url = `${API_URL}/taches/?page=${page}&page_size=100`;
        const params = new URLSearchParams();

        if (filters.statut && filters.statut !== 'tous') {
          params.append('statut', filters.statut);
        }
        if (filters.priorite && filters.priorite !== 'tous') {
          params.append('priorite', filters.priorite);
        }
        if (filters.recherche) {
          params.append('search', filters.recherche);
        }

        if (params.toString()) {
          url += '&' + params.toString();
        }

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();

          if (data.results && Array.isArray(data.results)) {
            allTasks = [...allTasks, ...data.results];
            if (data.next) {
              page++;
            } else {
              hasMore = false;
            }
          } else if (Array.isArray(data)) {
            allTasks = data;
            hasMore = false;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      const sortedTasks = allTasks.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setTaches(sortedTasks);
    } catch (error) {
      console.error("Erreur chargement tâches:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const statsEndpoint = isAdminOrManager
        ? `${API_URL}/taches/stats/`
        : `${API_URL}/taches/mes-stats/`;

      const response = await fetch(statsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Erreur stats:", error);
    }
  };

  const fetchReunions = async () => {
    setLoadingReunions(true);
    try {
      const token = getToken();

      let allReunions = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`${API_URL}/reunions/?page=${page}&page_size=100`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();

          if (data.results && Array.isArray(data.results)) {
            allReunions = [...allReunions, ...data.results];
            if (data.next) {
              page++;
            } else {
              hasMore = false;
            }
          } else if (Array.isArray(data)) {
            allReunions = data;
            hasMore = false;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      const formattedReunions = allReunions.map(reunion => ({
        id: reunion.id,
        titre: reunion.titre || "Sans titre",
        date_heure: reunion.date_heure
      }));

      setReunions(formattedReunions);
    } catch (error) {
      console.error("Erreur chargement réunions:", error);
      setReunions([]);
    } finally {
      setLoadingReunions(false);
    }
  };

  const handleOpenAddPopup = async () => {
    await fetchReunions();
    setShowAddPopup(true);
    setNewTask({
      description: '',
      reunion_id: '',
      responsable_id: '',
      deadline: '',
      priorite: 'moyenne'
    });
    setReunionParticipants([]);
  };

  const handleReunionChange = async (reunionId) => {
    setNewTask({ ...newTask, reunion_id: reunionId, responsable_id: '' });
    if (reunionId) {
      await fetchReunionParticipants(reunionId);
    } else {
      setReunionParticipants([]);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.description || !newTask.responsable_id || !newTask.deadline) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setAddingTask(true);
    try {
      const token = getToken();

      const deadlineDate = new Date(newTask.deadline);
      const formattedDeadline = deadlineDate.toISOString();

      const taskData = {
        description: newTask.description,
        reunion: parseInt(newTask.reunion_id) || null,
        responsable: parseInt(newTask.responsable_id),
        deadline: formattedDeadline,
        priorite: newTask.priorite,
        statut: 'a_faire'
      };

      console.log("Données envoyées:", taskData);

      const response = await fetch(`${API_URL}/taches/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taskData)
      });

      const responseData = await response.json();
      console.log("Réponse serveur:", responseData);

      if (response.ok) {
        setShowAddPopup(false);
        setFilters({
          statut: 'tous',
          priorite: 'tous',
          recherche: ''
        });
        await fetchTaches();
        await fetchStats();

        window.dispatchEvent(new CustomEvent('notifications-updated'));

        alert("✅ Tâche créée avec succès !");
      } else {
        alert("❌ Erreur: " + JSON.stringify(responseData));
      }
    } catch (error) {
      console.error("Erreur création tâche:", error);
      alert("❌ Erreur lors de la création de la tâche: " + error.message);
    } finally {
      setAddingTask(false);
    }
  };

  const handleOpenEditPopup = async (tache) => {
    await fetchReunions();
    setEditingTask(tache);

    let formattedDeadline = '';
    if (tache.deadline) {
      const date = new Date(tache.deadline);
      formattedDeadline = date.toISOString().slice(0, 16);
    }

    setEditTaskData({
      description: tache.description || '',
      reunion_id: tache.reunion || '',
      responsable_id: tache.responsable || '',
      deadline: formattedDeadline,
      priorite: tache.priorite || 'moyenne',
      statut: tache.statut || 'a_faire'
    });

    if (tache.reunion) {
      await fetchReunionParticipants(tache.reunion);
    }

    setShowEditPopup(true);
  };

  const handleEditReunionChange = async (reunionId) => {
    setEditTaskData({ ...editTaskData, reunion_id: reunionId, responsable_id: '' });
    if (reunionId) {
      await fetchReunionParticipants(reunionId);
    } else {
      setReunionParticipants([]);
    }
  };

  const handleUpdateTask = async () => {
    if (!editTaskData.description || !editTaskData.responsable_id || !editTaskData.deadline) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setUpdatingTask(true);
    try {
      const token = getToken();

      const deadlineDate = new Date(editTaskData.deadline);
      const formattedDeadline = deadlineDate.toISOString();

      const taskData = {
        description: editTaskData.description,
        reunion: parseInt(editTaskData.reunion_id) || null,
        responsable: parseInt(editTaskData.responsable_id),
        deadline: formattedDeadline,
        priorite: editTaskData.priorite,
        statut: editTaskData.statut
      };

      console.log("Données mise à jour:", taskData);

      const response = await fetch(`${API_URL}/taches/${editingTask.id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taskData)
      });

      const responseData = await response.json();

      if (response.ok) {
        setShowEditPopup(false);
        setEditingTask(null);
        await fetchTaches();
        await fetchStats();

        window.dispatchEvent(new CustomEvent('notifications-updated'));

        alert("✅ Tâche mise à jour avec succès !");
      } else {
        alert("❌ Erreur: " + JSON.stringify(responseData));
      }
    } catch (error) {
      console.error("Erreur mise à jour tâche:", error);
      alert("❌ Erreur lors de la mise à jour de la tâche");
    } finally {
      setUpdatingTask(false);
    }
  };

  const handleChangerStatut = async (tacheId, nouveauStatut) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/taches/${tacheId}/changer_statut/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut: nouveauStatut })
      });

      let responseData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        if (response.ok) {
          responseData = { success: true };
        } else {
          responseData = { error: text };
        }
      }

      if (response.ok) {
        await fetchTaches();
        await fetchStats();
        window.dispatchEvent(new CustomEvent('notifications-updated'));
      } else {
        console.error("Erreur changement statut:", responseData);
      }
    } catch (error) {
      console.error("Erreur réseau changement statut:", error);
    }
  };

  const handleMarquerTerminee = (tacheId) => {
    handleChangerStatut(tacheId, 'terminee');
  };

  const handleDeleteClick = (tache) => {
    setTaskToDelete(tache);
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setDeleting(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/taches/${taskToDelete.id}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setShowDeletePopup(false);
        setTaskToDelete(null);

        await fetchTaches();
        await fetchStats();

        window.dispatchEvent(new CustomEvent('notifications-updated'));
      } else {
        const error = await response.json();
        alert("❌ Erreur: " + JSON.stringify(error));
      }
    } catch (error) {
      console.error("Erreur suppression tâche:", error);
      alert("❌ Erreur lors de la suppression de la tâche");
    } finally {
      setDeleting(false);
    }
  };

  const getPriorityStyle = (priorite) => {
    const styles = {
      critique: { bg: '#fee2e2', color: '#ef4444' },
      haute: { bg: '#ffedd5', color: '#f97316' },
      moyenne: { bg: '#fef3c7', color: '#eab308' },
      basse: { bg: '#e6f7e6', color: '#16a34a' }
    };
    return styles[priorite] || styles.moyenne;
  };

  const getStatutStyle = (statut) => {
    const styles = {
      a_faire: { bg: '#f3f4f6', color: '#4b5563', icon: <AlertCircle size={12} />, text: 'À faire' },
      en_cours: { bg: '#dbeafe', color: '#2563eb', icon: <Clock size={12} />, text: 'En cours' },
      terminee: { bg: '#dcfce7', color: '#16a34a', icon: <CheckCircle size={12} />, text: 'Terminée' }
    };
    return styles[statut] || styles.a_faire;
  };

  const getResponsableList = () => {
    if (newTask.reunion_id && reunionParticipants.length > 0) {
      return reunionParticipants;
    }
    return users;
  };

  const getEditResponsableList = () => {
    if (editTaskData.reunion_id && reunionParticipants.length > 0) {
      return reunionParticipants;
    }
    return users;
  };

  // Formatage de la date pour l'affichage
  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'Date invalide';
    }
  };

  return (
    <Layout>
      <div style={styles.page}>
        <h1 style={styles.title}>{isAdminOrManager ? 'GESTION DES TÂCHES' : 'MES TÂCHES'}</h1>
        <p style={styles.subtitle}>
          {isAdminOrManager
            ? 'Gérez toutes les tâches de l\'équipe'
            : 'Gérez vos tâches et suivez leur progression'}
        </p>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{stats.total}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ ...styles.statValue }}>{stats.a_faire}</span>
            <span style={styles.statLabel}>À faire</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ ...styles.statValue }}>{stats.en_cours}</span>
            <span style={styles.statLabel}>En cours</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ ...styles.statValue }}>{stats.terminees}</span>
            <span style={styles.statLabel}>Terminées</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ ...styles.statValue }}>{stats.en_retard}</span>
            <span style={styles.statLabel}>En retard</span>
          </div>
        </div>

        {/* Top Bar */}
        <div style={styles.topBar}>
          <div style={styles.filterGroup}>
            <Filter size={18} color="#666" />
            <select
              value={filters.statut}
              onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
              style={styles.filterSelect}
            >
              <option value="tous">Tous les statuts</option>
              <option value="a_faire">À faire</option>
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminées</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <Flag size={18} color="#666" />
            <select
              value={filters.priorite}
              onChange={(e) => setFilters({ ...filters, priorite: e.target.value })}
              style={styles.filterSelect}
            >
              <option value="tous">Toutes priorités</option>
              <option value="basse">Basse</option>
              <option value="moyenne">Moyenne</option>
              <option value="haute">Haute</option>
              <option value="critique">Critique</option>
            </select>
          </div>

          <div style={styles.searchBox}>
            <Search size={16} color="#666" />
            <input
              type="text"
              placeholder="Rechercher une tâche..."
              value={filters.recherche}
              onChange={(e) => setFilters({ ...filters, recherche: e.target.value })}
              style={styles.searchInput}
            />
          </div>

          {isAdminOrManager && (
            <button style={styles.addBtn} onClick={handleOpenAddPopup}>
              <Plus size={18} /> Nouvelle tâche
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div style={styles.loading}>Chargement...</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Tâche</th>
                  <th>Priorité</th>
                  <th>Statut</th>
                  <th>Responsable</th>
                  <th>Échéance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {taches.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={styles.emptyRow}>
                      Aucune tâche trouvée
                    </td>
                  </tr>
                ) : (
                  taches.map((tache) => {
                    const priorityStyle = getPriorityStyle(tache.priorite);
                    const statusStyle = getStatutStyle(tache.statut);
                    const estEnRetard = tache.est_en_retard;

                    return (
                      <tr key={tache.id}>
                        <td style={styles.taskDescription}>
                          <strong>{tache.description}</strong>
                          {tache.reunion_titre && (
                            <div style={styles.taskMeta}>Réunion: {tache.reunion_titre}</div>
                          )}
                        </td>
                        <td>
                          <span style={{
                            ...styles.priorityBadge,
                            backgroundColor: priorityStyle.bg,
                            color: priorityStyle.color
                          }}>
                            <Flag size={12} />
                            {tache.priorite}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            ...styles.statusBadge,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {statusStyle.icon}
                            {statusStyle.text}
                          </span>
                          {estEnRetard && tache.statut !== 'terminee' && (
                            <span style={styles.retardBadge}>
                              <AlertCircle size={12} /> Retard
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={styles.responsableCell}>
                            <User size={14} color="#666" />
                            {tache.responsable_nom || 'Non assigné'}
                          </div>
                        </td>
                        <td>
                          {tache.deadline ? (
                            <div style={{
                              ...styles.deadline,
                              color: estEnRetard ? '#ef4444' : '#4b5563'
                            }}>
                              <Calendar size={14} />
                              {formatDate(tache.deadline)}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>Non définie</span>
                          )}
                        </td>
                        <td>
                          <div style={styles.actions}>
                            {tache.statut !== 'terminee' && (
                              <button
                                onClick={() => handleMarquerTerminee(tache.id)}
                                style={styles.checkButton}
                                title="Marquer comme terminée"
                              >
                                <CheckCircle size={16} />
                              </button>
                            )}

                            {(isAdminOrManager || authData.userId === tache.cree_par) && (
                              <button
                                onClick={() => handleOpenEditPopup(tache)}
                                style={styles.editButton}
                                title="Modifier"
                              >
                                <Edit size={16} />
                              </button>
                            )}

                            {(isAdminOrManager || authData.userId === tache.cree_par) && (
                              <button
                                onClick={() => handleDeleteClick(tache)}
                                style={styles.deleteButton}
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {taches.length > 0 && (
              <div style={styles.totalIndicator}>
                Total: {taches.length} tâche{taches.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Popup d'ajout de tâche */}
        {showAddPopup && (
          <div style={styles.overlay}>
            <div style={styles.popup}>
              <div style={styles.popupHeader}>
                <h3 style={styles.popupTitle}>Nouvelle tâche</h3>
                <button onClick={() => setShowAddPopup(false)} style={styles.closeButton}>
                  <X size={20} />
                </button>
              </div>

              <div style={styles.popupContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Description *</label>
                  <textarea
                    style={styles.textarea}
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    placeholder="Description de la tâche..."
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Réunion associée (optionnel)</label>
                  <select
                    style={styles.select}
                    value={newTask.reunion_id}
                    onChange={(e) => handleReunionChange(e.target.value)}
                    disabled={loadingReunions}
                  >
                    <option value="">
                      {loadingReunions ? "Chargement des réunions..." : "Sélectionner une réunion"}
                    </option>
                    {reunions.map(reunion => (
                      <option key={reunion.id} value={reunion.id}>
                        {reunion.titre}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Responsable *</label>
                  <select
                    style={styles.select}
                    value={newTask.responsable_id}
                    onChange={(e) => setNewTask({ ...newTask, responsable_id: e.target.value })}
                    disabled={loadingUsers || (newTask.reunion_id && reunionParticipants.length === 0)}
                  >
                    <option value="">
                      {loadingUsers ? "Chargement..." :
                        (newTask.reunion_id && reunionParticipants.length === 0) ? "Aucun participant dans cette réunion" :
                          "Sélectionner un responsable"}
                    </option>
                    {getResponsableList().map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.username || user.email}
                      </option>
                    ))}
                  </select>
                  {newTask.reunion_id && reunionParticipants.length > 0 && (
                    <p style={{ fontSize: '11px', color: '#5e17eb', marginTop: '4px' }}>
                      ℹ️ Participants de la réunion sélectionnée: {reunionParticipants.length}
                    </p>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Date d'échéance *</label>
                  <input
                    type="datetime-local"
                    style={styles.input}
                    value={newTask.deadline}
                    onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Priorité *</label>
                  <select
                    style={styles.select}
                    value={newTask.priorite}
                    onChange={(e) => setNewTask({ ...newTask, priorite: e.target.value })}
                  >
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                    <option value="critique">Critique</option>
                  </select>
                </div>
              </div>

              <div style={styles.popupFooter}>
                <button onClick={() => setShowAddPopup(false)} style={styles.cancelButton}>
                  Annuler
                </button>
                <button
                  onClick={handleAddTask}
                  style={styles.saveButton}
                  disabled={addingTask}
                >
                  {addingTask ? 'Création...' : 'Créer la tâche'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popup d'édition de tâche */}
        {showEditPopup && editingTask && (
          <div style={styles.overlay}>
            <div style={styles.popup}>
              <div style={styles.popupHeader}>
                <h3 style={styles.popupTitle}>Modifier la tâche</h3>
                <button onClick={() => setShowEditPopup(false)} style={styles.closeButton}>
                  <X size={20} />
                </button>
              </div>

              <div style={styles.popupContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Description *</label>
                  <textarea
                    style={styles.textarea}
                    value={editTaskData.description}
                    onChange={(e) => setEditTaskData({ ...editTaskData, description: e.target.value })}
                    rows={3}
                    placeholder="Description de la tâche..."
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Statut *</label>
                  <select
                    style={styles.select}
                    value={editTaskData.statut}
                    onChange={(e) => setEditTaskData({ ...editTaskData, statut: e.target.value })}
                  >
                    <option value="a_faire">À faire</option>
                    <option value="en_cours">En cours</option>
                    <option value="terminee">Terminée</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Réunion associée (optionnel)</label>
                  <select
                    style={styles.select}
                    value={editTaskData.reunion_id}
                    onChange={(e) => handleEditReunionChange(e.target.value)}
                    disabled={loadingReunions}
                  >
                    <option value="">
                      {loadingReunions ? "Chargement des réunions..." : "Sélectionner une réunion"}
                    </option>
                    {reunions.map(reunion => (
                      <option key={reunion.id} value={reunion.id}>
                        {reunion.titre}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Responsable *</label>
                  <select
                    style={styles.select}
                    value={editTaskData.responsable_id}
                    onChange={(e) => setEditTaskData({ ...editTaskData, responsable_id: e.target.value })}
                    disabled={loadingUsers || (editTaskData.reunion_id && reunionParticipants.length === 0)}
                  >
                    <option value="">
                      {loadingUsers ? "Chargement..." :
                        (editTaskData.reunion_id && reunionParticipants.length === 0) ? "Aucun participant dans cette réunion" :
                          "Sélectionner un responsable"}
                    </option>
                    {getEditResponsableList().map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.username || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Date d'échéance *</label>
                  <input
                    type="datetime-local"
                    style={styles.input}
                    value={editTaskData.deadline}
                    onChange={(e) => setEditTaskData({ ...editTaskData, deadline: e.target.value })}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Priorité *</label>
                  <select
                    style={styles.select}
                    value={editTaskData.priorite}
                    onChange={(e) => setEditTaskData({ ...editTaskData, priorite: e.target.value })}
                  >
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                    <option value="critique">Critique</option>
                  </select>
                </div>
              </div>

              <div style={styles.popupFooter}>
                <button onClick={() => setShowEditPopup(false)} style={styles.cancelButton}>
                  Annuler
                </button>
                <button
                  onClick={handleUpdateTask}
                  style={styles.saveButton}
                  disabled={updatingTask}
                >
                  {updatingTask ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popup de confirmation de suppression */}
        {showDeletePopup && taskToDelete && (
          <div style={styles.overlay}>
            <div style={styles.deletePopup}>
              <div style={styles.deleteIcon}>
                <AlertCircle size={40} color="#ef4444" />
              </div>
              <h3 style={styles.deleteTitle}>Confirmer la suppression</h3>
              <p style={styles.deleteMessage}>
                Êtes-vous sûr de vouloir supprimer définitivement la tâche<br />
                <strong>"{taskToDelete.description}"</strong> ?
              </p>
              <p style={styles.deleteWarning}>
                Cette action est irréversible.
              </p>
              <div style={styles.deleteActions}>
                <button
                  onClick={() => setShowDeletePopup(false)}
                  style={styles.deleteCancelButton}
                  disabled={deleting}
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  style={styles.deleteConfirmButton}
                  disabled={deleting}
                >
                  {deleting ? 'Suppression...' : 'Oui, supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page: {
    padding: "22px",
    background: "#f7f6ff",
    minHeight: "100vh",
    borderRadius: "16px"
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  statsBar: {
    display: "flex",
    gap: "24px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px"
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "18px",
    gap: "12px",
    flexWrap: "wrap"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "white",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  filterSelect: {
    padding: "8px",
    border: "none",
    background: "transparent",
    outline: "none",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "140px"
  },
  searchBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    background: "white",
    padding: "0 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    minWidth: "250px"
  },
  searchInput: {
    flex: 1,
    padding: "10px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    background: "transparent"
  },
  addBtn: {
    backgroundColor: "#5e17eb",
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  tableContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    overflowX: "auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px"
  },
  taskDescription: {
    fontWeight: "500",
    color: "#1f1f2e"
  },
  taskMeta: {
    fontSize: "11px",
    color: "#9ca3af",
    marginTop: "2px"
  },
  emptyRow: {
    textAlign: "center",
    padding: "40px",
    color: "#9ca3af"
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#666"
  },
  priorityBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
    textTransform: "capitalize"
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500"
  },
  retardBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: "8px",
    color: "#ef4444",
    fontSize: "11px",
    fontWeight: "500"
  },
  responsableCell: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#4b5563"
  },
  deadline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#4b5563"
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "center"
  },
  checkButton: {
    color: "#16a34a",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#dcfce7",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "32px"
  },
  editButton: {
    color: "#5e17eb",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#e9d9ff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "32px"
  },
  deleteButton: {
    color: "#ef4444",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#fee2e2",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "32px"
  },
  totalIndicator: {
    textAlign: 'right',
    marginTop: '15px',
    padding: '8px 12px',
    fontSize: '13px',
    color: '#5e17eb',
    fontWeight: '500',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9f9ff'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  popup: {
    background: 'white',
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  popupHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  popupTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1f1f2e',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
    padding: 4,
    borderRadius: 8,
  },
  popupContent: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    color: '#4b5563',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  popupFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    padding: '8px 16px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '8px 20px',
    background: '#5e17eb',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
  },
  deletePopup: {
    background: 'white',
    borderRadius: 16,
    padding: 32,
    width: '90%',
    maxWidth: 450,
    textAlign: 'center',
  },
  deleteIcon: {
    marginBottom: 16,
  },
  deleteTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1f1f2e',
    marginBottom: 12,
  },
  deleteMessage: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  deleteWarning: {
    fontSize: 13,
    color: '#ef4444',
    background: '#fee2e2',
    padding: 10,
    borderRadius: 8,
    marginBottom: 20,
  },
  deleteActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
  },
  deleteCancelButton: {
    padding: '10px 20px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  deleteConfirmButton: {
    padding: '10px 20px',
    background: '#ef4444',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
  },
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);